import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { getErrorMessage } from "@/lib/error-message";

type AppRole = "admin" | "staff" | "viewer" | "super_admin";

interface Profile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  phone: string | null;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  roles: AppRole[];
  hasRole: (role: AppRole) => boolean;
  isSuperAdmin: boolean;
  featureFlags: Record<string, boolean>;
  featureFlagsStatus: "loading" | "loaded" | "error";
  refreshFeatureFlags: () => Promise<void>;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [featureFlagsStatus, setFeatureFlagsStatus] = useState<"loading" | "loaded" | "error">("loading");
  const [isLoading, setIsLoading] = useState(true);
  // Id of the user whose profile/roles last finished loading. SIGNED_IN sets
  // the session a tick before fetchUserData runs; until this matches the
  // session's user, report loading so role-gated routes don't flash Access
  // Denied (#990). A same-user SIGNED_IN (tab refocus) doesn't reopen the gap.
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);
  // The user the current session belongs to, so a slow fetch for a previous
  // user can't land on top of the next one's roles.
  const latestUserIdRef = useRef<string | null>(null);

  // Bumped on every flag read so a slow earlier response (the initial load, or an
  // overtaken refresh) can't land on top of a newer one.
  const flagsRequestSeq = useRef(0);

  // featureFlags stays {} until the first read lands, and isLoading doesn't wait for
  // it, so flag guards must wait on featureFlagsStatus rather than read {} as "every
  // flag off" (#627). The table is publicly readable. Also exposed as
  // refreshFeatureFlags so the sidebar's "Soon" chips follow a toggle at
  // /admin/feature-flags without a reload (#700). Never rejects.
  const loadFeatureFlags = useCallback(async () => {
    const seq = ++flagsRequestSeq.current;
    const fail = (message: string) => {
      console.error("Error fetching feature flags:", message);
      // Once flags have loaded, a failed re-read keeps them rather than locking out
      // every flag-gated page.
      setFeatureFlagsStatus((s) => (s === "loading" ? "error" : s));
    };
    try {
      const { data, error } = await supabase.from("feature_flags").select("module_key, is_enabled");
      if (seq !== flagsRequestSeq.current) return;
      if (error) return fail(error.message);
      const map: Record<string, boolean> = {};
      (data ?? []).forEach((f: { module_key: string; is_enabled: boolean }) => {
        map[f.module_key] = f.is_enabled;
      });
      setFeatureFlags(map);
      setFeatureFlagsStatus("loaded");
    } catch (err) {
      if (seq === flagsRequestSeq.current) fail(getErrorMessage(err, String(err)));
    }
  }, []);

  useEffect(() => {
    loadFeatureFlags();
  }, [loadFeatureFlags]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, newSession) => {
        setSession(newSession);
        latestUserIdRef.current = newSession?.user?.id ?? null;
        if (newSession?.user) {
          setTimeout(() => fetchUserData(newSession.user.id), 0);
        } else {
          setProfile(null);
          setRoles([]);
          setLoadedUserId(null);
          setIsLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      latestUserIdRef.current = s?.user?.id ?? null;
      if (s?.user) {
        fetchUserData(s.user.id);
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchUserData(userId: string) {
    try {
      const [profileRes, rolesRes] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", userId).single(),
        supabase.from("user_roles").select("role").eq("user_id", userId),
      ]);
      if (latestUserIdRef.current !== userId) return;
      if (profileRes.data) setProfile(profileRes.data as Profile);
      if (rolesRes.data) setRoles((rolesRes.data as { role: AppRole }[]).map((r) => r.role));
    } catch (err) {
      console.error("Error fetching user data:", err);
    } finally {
      if (latestUserIdRef.current === userId) {
        setLoadedUserId(userId);
        setIsLoading(false);
      }
    }
  }

  const isSuperAdmin = roles.includes("super_admin");
  const hasRole = (role: AppRole) => isSuperAdmin || roles.includes(role);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setRoles([]);
    latestUserIdRef.current = null;
    setLoadedUserId(null);
  };

  const sessionUserId = session?.user?.id ?? null;
  const authLoading = isLoading || (sessionUserId !== null && loadedUserId !== sessionUserId);

  return (
    <AuthContext.Provider
      value={{ session, user: session?.user ?? null, profile, roles, hasRole, isSuperAdmin, featureFlags, featureFlagsStatus, refreshFeatureFlags: loadFeatureFlags, isLoading: authLoading, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// The provider and its hook belong together; splitting useAuth into its own file
// would touch every importer for no behavioural gain.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
